import React, { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { FinancialConvoContext } from "FinancialConvoContextWrapper";
import { FinancialConvo } from "models/FinancialConvo";
import { useAuthContext } from "../../../AuthContextWrapper";
import { OUTCOMES } from "../../../constants";
import {
  SingleSelectButtonForm,
  SelectButton,
} from "../shared/SingleSelectButtonForm";
import { QuestioningIcon, OkIcon, ShadesIcon } from "../shared/EmotionIcons";
import { enumToStringDictionary } from "@theatticusapp/atticus-shared-components";

export enum Outcomes {
  beginner = "Beginner",
  intermediate = "Intermediate",
  advanced = "Advanced",
}

const iconStyles = { width: 60, height: 60, padding: "6px" } as const;
const iconViewBox = "0 0 48 48";

const icons: { [key: string]: JSX.Element } = {
  beginner: <QuestioningIcon sx={iconStyles} viewBox={iconViewBox} />,
  intermediate: <OkIcon sx={iconStyles} viewBox={iconViewBox} />,
  advanced: <ShadesIcon sx={iconStyles} viewBox={iconViewBox} />,
};

const responses: { [key: string]: string } = {
  beginner:
    "We get it—finance can be confusing! The excitement and opportunity that comes from inheriting assets can sometimes get overshadowed by how complex it can be.",
  intermediate:
    "This process can get confusing at times, so the time you’ve spent learning about personal finance will definitely help move things along.",
  advanced:
    "Nice! There are just a few considerations you’ll need to be aware of to continue to make great financial decisions.",
};

const outcomesDictionary = enumToStringDictionary(Outcomes);
const feelingSelectButtons: SelectButton[] = [];
Object.keys(outcomesDictionary).forEach((key: string) =>
  feelingSelectButtons.push({
    key,
    text: outcomesDictionary[key],
    icon: icons[key],
    response: responses[key],
  })
);

export const FinancialExperienceScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow, activeEstate, user } = useAuthContext()
  const { latestFinancialConvo, setLatestFinancialConvo } = useContext(
    FinancialConvoContext
  );
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);

  const handleComplete = async (
    financialExperience: string
  ): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    if (!activeEstate?.id || !user?.id) {
      throw "No estate or user ID";
    }

    const financialConvo = latestFinancialConvo || new FinancialConvo();
    financialConvo.financialExperience = financialExperience;

    try {
      await financialConvo.saveRequest(activeEstate?.id, user?.id);
      setLatestFinancialConvo && setLatestFinancialConvo(financialConvo);
    } catch (error) {
      throw new Error("could not save data");
    }

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  return currentScreen ? (
    <FlowScreenWrapper
      title="What’s your experience level with investing, managing, and maintaining investment accounts?"
      subTitle="When you inherit assets, you may need to rebalance a portfolio and consider tax implications of your inheritance."
    >
      <SingleSelectButtonForm
        initChecked={latestFinancialConvo?.financialExperience}
        selectButtons={feelingSelectButtons}
        required
        onComplete={handleComplete}
        onBack={() => navigate(-1)}
      />
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
